import { Field, FlexProps } from '@chakra-ui/react';
import { ActiveDocumentType } from '@monetize/types/app';
import { FC, useEffect, useRef, useState } from 'react';
import { AiOutlineLink } from 'react-icons/ai';
import { MdInfo } from 'react-icons/md';
import {
  MBox,
  MButton,
  MFlex,
  MFormField,
  MIcon,
  MInput,
  MSpinner,
  MText,
  MTooltip,
} from '../../../../../components/Monetize';
import { MFileUploadButton } from '../../../../../components/Monetize/MFileUploadButton';
import { QuoteDocumentLinkV2 } from '../../../../../components/Quotes/QuoteDocumentLinkV2';
import { ACCEPT_FILES } from '../../../../../constants/misc';

interface QuoteDocumentsViewItemProps extends FlexProps {
  label: string;
  allowLink?: boolean;
  allowFileUpload?: boolean;
  accept?: (keyof typeof ACCEPT_FILES)[];
  activeItem: ActiveDocumentType;
  linkUrl: string;
  savedItemText?: string;
  disabled?: boolean;
  isLoading?: boolean;
  isReadOnly?: boolean;
  showControlButton?: boolean;
  dndLabel?: string;
  onSetActiveItem?: (type: ActiveDocumentType) => void;
  onAddItem?: (
    data: { type: 'file'; value: File } | { type: 'link'; value: string },
  ) => void;
  onRemoveItem?: () => void;
  onGotoSavedItem: () => void;
  saveBtnContent?: (data: { value: string; error?: string }) => React.ReactNode;
}

export const QuoteDocumentsViewItem: FC<QuoteDocumentsViewItemProps> = ({
  label,
  allowLink,
  allowFileUpload = true,
  accept = ['pdf'],
  activeItem,
  linkUrl,
  savedItemText,
  disabled = false,
  isLoading = false,
  isReadOnly = false,
  onSetActiveItem,
  onAddItem,
  onRemoveItem,
  onGotoSavedItem,
  showControlButton = false,
  dndLabel,
  saveBtnContent,
  ...rest
}: QuoteDocumentsViewItemProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [internalLinkUrlValue, setInternalLinkUrlValue] =
    useState<string>(linkUrl);
  const [inputError, setInputError] = useState<null | { message: string }>(
    null,
  );
  useEffect(() => {
    setInternalLinkUrlValue(linkUrl);
  }, [linkUrl]);

  if (isReadOnly && !savedItemText) {
    return null;
  }

  // Show error message if input is not a proper URL (onBlur)
  function handleInputValidation() {
    if (linkUrl && inputRef.current) {
      const { typeMismatch, tooLong } = inputRef.current.validity;
      if (typeMismatch || tooLong) {
        setInputError({ message: 'A valid URL is required' });
        return;
      }
    }
    setInputError(null);
  }
  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      if (inputError) {
        return;
      }
      onAddItem && onAddItem({ type: 'link', value: inputRef.current?.value! });
    }
  };
  const shouldShowUploadControlButtons =
    (activeItem === 'none' || activeItem === 'file') &&
    !savedItemText &&
    !isReadOnly;

  const labelContent = (
    <Field.Root>
      <Field.Label
        color="tGray.darkPurple"
        fontWeight="400"
        display="flex"
        alignItems="center"
        gap="2"
        css={{ textWrap: 'nowrap' }}
        mb="0"
      >
        <MText as="span">{label}</MText>
        {!allowFileUpload && !isReadOnly && (
          <MBox display="inline-flex" alignItems="center">
            <MIcon as={MdInfo} />
          </MBox>
        )}
        {isLoading && <MSpinner size="xs" ml="2" />}
      </Field.Label>
    </Field.Root>
  );

  return (
    <MFlex flexDir={'column'} w="100%" {...rest}>
      {allowFileUpload || isReadOnly ? (
        labelContent
      ) : (
        <MTooltip
          usePopover
          placement="top"
          size="sm"
          label={
            <MBox fontSize="xs">
              {`If you have an online ${label}, you can link to it here. Otherwise upload your ${label} as a document.`}
            </MBox>
          }
        >
          {labelContent}
        </MTooltip>
      )}

      <MBox minWidth={0}>
        {shouldShowUploadControlButtons && (
          <MFlex alignItems="center">
            {allowFileUpload && (
              <MFileUploadButton
                accept={accept}
                onFileUpload={(file) =>
                  onAddItem && onAddItem({ type: 'file', value: file })
                }
                maxFileSize={10}
                onCancel={() => {
                  onSetActiveItem && onSetActiveItem('none');
                }}
                renderBoxContent={({ open }) => {
                  return (
                    <MButton
                      onClick={() => {
                        onSetActiveItem && onSetActiveItem('file');
                        open();
                      }}
                      variant="tertiary"
                      ml="-3"
                      size="sm"
                      isDisabled={isReadOnly || isLoading}
                    >
                      + Add Document
                    </MButton>
                  );
                }}
              />
            )}

            {allowLink && (
              <>
                {allowFileUpload && (
                  <MText color="tGray.darkPurple" mx="3">
                    or
                  </MText>
                )}
                <MButton
                  leftIcon={<AiOutlineLink />}
                  onClick={() =>
                    !savedItemText && onSetActiveItem && onSetActiveItem('link')
                  }
                  variant="tertiary"
                  minW="auto"
                  px="2"
                  ml={allowFileUpload ? undefined : '-3'}
                  isDisabled={isReadOnly || isLoading}
                >
                  Add Link
                </MButton>
              </>
            )}
          </MFlex>
        )}

        {!savedItemText && !disabled && activeItem === 'link' && (
          <MFlex align="flex-start" gap={2}>
            <MFormField error={inputError}>
              <MInput
                ref={inputRef}
                placeholder="https://"
                maxLength={255}
                type="url"
                onBlur={handleInputValidation}
                onChange={(event) => {
                  if (saveBtnContent) {
                    setInternalLinkUrlValue(event.target.value);
                    if (inputRef.current) {
                      const { typeMismatch, tooLong } =
                        inputRef.current.validity;
                      if (typeMismatch || tooLong) {
                        setInputError({ message: 'A valid URL is required' });
                        return;
                      }
                    }
                    setInputError(null);
                  } else {
                    onAddItem &&
                      onAddItem({ type: 'link', value: event.target.value });
                  }
                }}
                onKeyDown={handleKeyDown}
                value={saveBtnContent ? internalLinkUrlValue : linkUrl}
              />
            </MFormField>
            {saveBtnContent &&
              saveBtnContent({
                error: inputError?.message,
                value: inputRef.current?.value ?? '',
              })}
          </MFlex>
        )}

        {savedItemText && (
          <MBox mr="auto" mt="1">
            <QuoteDocumentLinkV2
              showDeleteIcon
              fileName={savedItemText}
              isReadOnly={isReadOnly}
              disabled={disabled}
              onClick={onGotoSavedItem}
              onRemoveItem={onRemoveItem}
              showLinkIcon={!!linkUrl}
            />
          </MBox>
        )}
      </MBox>
    </MFlex>
  );
};
